import { ObjectId } from 'bson';
import chunk from 'lodash/chunk';
import md5 from 'md5';
import moment from 'moment';
import { i18n } from 'src/i18n';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import Errors from 'src/modules/shared/error/errors';
import Importer from 'src/modules/shared/importer/importer';
import yupImporterSchemas from '../yup/yupImporterSchemas';

async function importRow(
  dispatch,
  actions,
  importer,
  importFn,
  row,
) {
  try {
    const importableRow = await importer.castForImport(row);
    const importHash = md5(JSON.stringify(importableRow));
    await importFn(importableRow, importHash);

    dispatch({
      type: actions.IMPORT_BATCH_SUCCESS,
      payload: {
        line: row._line,
      },
    });
  } catch (error) {
    dispatch({
      type: actions.IMPORT_BATCH_ERROR,
      payload: {
        line: row._line,
        errorMessage: Errors.selectMessage(error),
      },
    });
  }
}

export default (
  prefix,
  selectors,
  importFn,
  importFields,
  templateFileName,
  batchSize = 50,
) => {
  const actions = {
    RESETED: `${prefix}_RESETED`,

    FILE_READ_ERROR: `${prefix}_FILE_READ_ERROR`,
    FILE_READ_SUCCESS: `${prefix}_FILE_READ_SUCCESS`,
    SET_FIELDS: `${prefix}_SET_FIELDS`,

    IMPORT_STARTED: `${prefix}_IMPORT_STARTED`,
    IMPORT_ERROR: `${prefix}_IMPORT_ERROR`,
    IMPORT_PAUSED: `${prefix}_IMPORT_PAUSED`,
    IMPORT_SUCCESS: `${prefix}_IMPORT_SUCCESS`,

    IMPORT_BATCH_ERROR: `${prefix}_IMPORT_BATCH_ERROR`,
    IMPORT_BATCH_SUCCESS: `${prefix}_IMPORT_BATCH_SUCCESS`,

    doReset: () => {
      return {
        type: actions.RESETED,
      };
    },

    doPause: () => {
      return {
        type: actions.IMPORT_PAUSED,
      };
    },

    doImport: (props) => async (dispatch, getState) => {
      try {
        let newFolder: any = null;
        let asyncImport = true;

        const currentSettings =
          AuthCurrentTenant.getSettings();

        const pendingRows = selectors.selectPendingRows(
          getState(),
        );

        if (props?.newFolder) {
          asyncImport = false;

          newFolder = {
            _status: 'PENDING',
            _line: 1,
            id: new ObjectId().toString(),
            name: props?.newFolder,
            type: 'folder',
            status: 'opened',
            number: 0,
            lockerStudioImport: true,
          };

          newFolder._id = newFolder.id;

          pendingRows.unshift(newFolder);

          dispatch({
            type: actions.FILE_READ_SUCCESS,
            payload: pendingRows,
          });
        }

        dispatch({
          type: actions.IMPORT_STARTED,
        });

        const newFields = selectors.selectFields(
          getState(),
        );

        if (newFolder) {
          const oldToNewIdMap = {};

          pendingRows.forEach((pendingRow) => {
            if (pendingRow.documentId) {
              oldToNewIdMap[pendingRow.documentId] =
                new ObjectId().toString();
            }
          });

          pendingRows
            .filter((row) => row.type !== 'folder')
            .map((row, index) => {
              row.parentForm =
                oldToNewIdMap[row.parentId] || null;
              row.id =
                oldToNewIdMap[row.documentId] ||
                new ObjectId().toString();
              row._id = row.id;
              row.parentResource = newFolder.id;
              row.number = index + 1;
              row.lockerStudioImport = true;

              if (!row.name) {
                row.name = row.formTemplateName;
              }

              if (row.parentId) {
                row.type = 'attachment-form';
              } else {
                row.type = 'form';
              }

              Object.keys(row).forEach((key) => {
                const formKey =
                  currentSettings?.config?.formKeys?.find(
                    (formKey) => formKey.key === key,
                  );

                if (
                  row[key] === null ||
                  row[key] === undefined
                ) {
                  delete row[key];
                  return;
                }

                if (formKey?.type === 'number') {
                  if (typeof row[key] === 'string') {
                    row[key] = row[key].replace(/,/g, '.');
                    row[key] = row[key].replace(/\s/g, '');
                  }

                  //Check if the value is a integer add two decimals
                  row[key] = Number(row[key]);

                  if (Number.isInteger(row[key])) {
                    row[key] = parseFloat(
                      row[key].toFixed(2),
                    );
                  }
                }

                if (formKey?.type === 'date') {
                  row[key] = moment(row[key]).format(
                    'YYYY-MM-DD',
                  );
                }

                if (formKey?.type === 'time') {
                  row[key] = moment(row[key]).format(
                    'HH:mm',
                  );
                }

                if (formKey?.type === 'dateTime') {
                  row[key] = moment(row[key]).format(
                    'YYYY-MM-DD HH:mm',
                  );
                }
              });

              if (
                row.deleted === null ||
                row.deleted === undefined
              ) {
                row.deleted = false;
              }

              delete row.documentId;
              delete row.parentId;
            });

          newFields.push({
            name: 'parentForm',
            label: 'parentForm',
            schema: yupImporterSchemas.string(null),
          });

          newFields.push({
            name: 'id',
            label: 'id',
            schema: yupImporterSchemas.string(null),
          });

          newFields.push({
            name: '_id',
            label: '_id',
            schema: yupImporterSchemas.string(null),
          });

          newFields.push({
            name: 'parentResource',
            label: 'parentResource',
            schema: yupImporterSchemas.string(null),
          });

          newFields.push({
            name: 'number',
            label: 'number',
            schema: yupImporterSchemas.integer(null),
          });

          newFields.push({
            name: 'lockerStudioImport',
            label: 'lockerStudioImport',
            schema: yupImporterSchemas.boolean(null),
          });

          newFields.push({
            name: 'name',
            label: 'name',
            schema: yupImporterSchemas.string(null),
          });

          newFields.push({
            name: 'type',
            label: 'type',
            schema: yupImporterSchemas.string(null),
          });

          newFields.push({
            name: 'deleted',
            label: 'deleted',
            schema: yupImporterSchemas.boolean(null),
          });
        }

        const importer = new Importer(
          newFields?.length > 0 ? newFields : importFields,
        );

        const pendingBatches = chunk(
          pendingRows,
          batchSize,
        );

        if (asyncImport) {
          for (const batch of pendingBatches) {
            const paused = !selectors.selectImporting(
              getState(),
            );

            if (paused) {
              return;
            }

            for (const row of batch) {
              await importRow(
                dispatch,
                actions,
                importer,
                importFn,
                row,
              );
            }
          }
        } else {
          for (const batch of pendingBatches) {
            const paused = !selectors.selectImporting(
              getState(),
            );

            if (paused) {
              return;
            }

            await Promise.all(
              batch.map((row) =>
                importRow(
                  dispatch,
                  actions,
                  importer,
                  importFn,
                  row,
                ),
              ),
            );
          }
        }

        dispatch({
          type: actions.IMPORT_SUCCESS,
        });
      } catch (error) {
        Errors.handle(error);

        dispatch({
          type: actions.IMPORT_ERROR,
        });
      }
    },

    doDownloadTemplate: () => async (_dispatch) => {
      const importer = new Importer(importFields);
      importer.downloadTemplate(templateFileName);
    },

    doReadFile: (file) => async (dispatch) => {
      try {
        let importer = new Importer(importFields);

        let rawData: any =
          await importer.convertExcelFileToJson(
            file,
            false,
          );

        const additionalFields = rawData?.[0]?.find((row) =>
          row.includes('$.'),
        );

        const documentIds = rawData?.[0]?.find((row) =>
          row.includes('documentId'),
        );

        if (additionalFields || documentIds) {
          const fields = rawData?.[0]?.map((field) => {
            const row = field?.replace('$.', '');

            return {
              name: field,
              label:
                (
                  row?.split('.')?.[1] ||
                  row?.split('.')?.[0]
                )?.trim() || row,
              schema: yupImporterSchemas.string(null),
            };
          });

          importer = new Importer(fields);

          dispatch({
            type: actions.SET_FIELDS,
            payload: fields,
          });
        }

        rawData = await importer.convertExcelFileToJson(
          file,
        );

        if (!rawData || !rawData.length) {
          throw new Error(
            i18n('importer.errors.invalidFileEmpty'),
          );
        }

        rawData = await Promise.all(
          rawData.map(async (row, index) => {
            return await importer.castForDisplay(
              row,
              index,
            );
          }),
        );

        dispatch({
          type: actions.FILE_READ_SUCCESS,
          payload: rawData,
        });
      } catch (error) {
        console.error(error);
        dispatch({
          type: actions.FILE_READ_ERROR,
          payload: error,
        });
      }
    },
  };

  return actions;
};
